import { type ReactNode, useEffect, useRef, useState } from "react";
import { BOARD_CARD_DRAWER_PROPS } from "./boardAttributes";
import styles from "./BoardCardDrawer.module.scss";

interface ShownDrawer {
    content: ReactNode;
    /** Opened after the card mounted, so it plays the enter motion. */
    isEntering: boolean;
}

const isEmptyContent = (content: ReactNode) =>
    content === null ||
    content === undefined ||
    content === false ||
    content === "";

/** Settles once `element`'s own CSS transitions end; at once in jsdom, which has no Web Animations API. */
const whenTransitionsEnd = (element: Element) =>
    Promise.allSettled(
        ("getAnimations" in element ? element.getAnimations() : []).map(
            (animation) => animation.finished,
        ),
    );

/**
 * `BoardCard`'s drawer, behind the card's bottom edge with its top corners
 * tucked under the card. Empty content is closed. Opening after the card
 * mounted slides it out and closing slides it back, keeping the last content
 * (hidden from assistive tech) until that ends; a card that mounts
 * with content shows it at once. Motion lives in BoardCardDrawer.module.scss.
 */
export const BoardCardDrawer = ({ children }: { children: ReactNode }) => {
    const isOpen = !isEmptyContent(children);
    const [shown, setShown] = useState<ShownDrawer | null>(() =>
        isOpen ? { content: children, isEntering: false } : null,
    );
    if (isOpen && shown?.content !== children) {
        setShown({ content: children, isEntering: shown?.isEntering ?? true });
    }

    const slotRef = useRef<HTMLDivElement>(null);
    const isClosing = !isOpen && shown !== null;
    useEffect(() => {
        const slot = slotRef.current;
        if (!isClosing || slot === null) {
            return undefined;
        }
        let isCurrent = true;
        whenTransitionsEnd(slot).then(() => {
            if (isCurrent) {
                setShown(null);
            }
        });
        return () => {
            isCurrent = false;
        };
    }, [isClosing]);

    if (shown === null) {
        return null;
    }
    return (
        <div
            ref={slotRef}
            className={styles.slot}
            {...BOARD_CARD_DRAWER_PROPS}
            data-open={isOpen}
            data-entering={shown.isEntering || undefined}
            aria-hidden={isClosing || undefined}
        >
            <div className={styles.clip}>
                <div className={styles.drawer}>{shown.content}</div>
            </div>
        </div>
    );
};
